import React, { useMemo } from 'react';
import sortBy from 'lodash.sortby';
import { line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import Localization from '../../../Localization';
import { metrics, recursiveNest } from '../../Report';
import { geoKey, formatAs } from '../../../settings.js';

import './styles.scss';

export default function Emissions(props) {
  const { investments, scale, geoType, geoColors, yearExtent, sankeyHeight, tooltipData, setTooltipData } = props;

  const xScale = scaleLinear().range([0, metrics.emissionsInnerWidth]);
  const yScale = scaleLinear().range([0, metrics.emissionsInnerHeight]).nice();
  const lineGenerator = line().x(d => xScale(d.name)).y(d => -yScale(d.cumulativeEmissions));

  let lineData = useMemo(() => recursiveNest(
    investments,
    [
      {
        key: d => geoKey[geoType](d),
        value: 'co2emissions',
      },
      {
        // key: d => d.year,
        key: d => d.year === 'TBD' ? 2030 : d.year,
        value: 'co2emissions',
      },
      {
        key: d => d.plant,
        value: 'co2emissions',
      },
    ],
    0,
    false
  ), [investments, geoType]);
  lineData = useMemo(() => (
      lineData.map((d, i) => {
        d.groups = sortBy(d.groups.filter(g => !isNaN(g.name)), g => g.name);
        const years = [];
        let cumulativeEmissions = 0;
        for (let y=yearExtent[0]; y<=yearExtent[1]; y++) {
          const year = d.groups.find(d => d.name === y) || { name: y, value: 0, groups: [] };
          cumulativeEmissions += year.value;
          year.cumulativeEmissions = cumulativeEmissions;
          years.push(year);
        }
        d.years = years;
        d.cumulativeEmissions = d.years.map(y => y.cumulativeEmissions).reduce((a, v) => a + v, 0);
        d.color = geoColors(d.value);
        return d;
      }).filter(d => d.cumulativeEmissions)
  ), [geoColors, lineData, yearExtent]);

  xScale.domain(yearExtent);
  const maxEmissions = Math.max(...lineData.map(d => d.years.map(g => g.cumulativeEmissions))
    .reduce((a, v) => a.concat(v), []));
  yScale.domain([0, maxEmissions]);

  const xOffset = metrics.margin.small * 3;

  function hoverPath(event, selection) {
    if ('clientX' in event && selection) {
      const x = (event.clientX / scale) - xOffset;
      const iX = xScale.invert(x);
      const year = selection.groups.map(s => s.name).reduce((p, c) => Math.abs(c - iX) < Math.abs(p - iX) ? c : p);
      const datum = selection.years.find(s => s.name === year);

      if (tooltipData) {
        if (tooltipData.year === datum.name && tooltipData.name === selection.name) {
          return;
        }
      }
      metrics.tooltip.height = (datum.groups.length + 1) * metrics.font.medium;
      setTooltipData({
        year: datum.name,
        total: datum.value,
        cumulativeEmissions: datum.cumulativeEmissions,
        name: selection.name,
        color: selection.color,
        projects: datum.groups,
        style: {
          marginLeft: `${xOffset + xScale(datum.name)}px`,
          marginTop: `${(yScale(datum.cumulativeEmissions) + (metrics.margin.small * 1.75)) * -1}px`,
        }
      });
    } else {
      setTooltipData(null);
    }
  }

  return (
    <g
      id='annualEmissions'
      className='Emissions'
      transform={`translate(0, ${sankeyHeight})`}
      fill='black'
    >
      <rect width={metrics.width} height={metrics.emissionsHeight} fill='white' />
      <rect width={metrics.width} height={metrics.emissionsHeight} fill='#EBEBE6' fillOpacity="0.5"
      onMouseOver={e => hoverPath(e, null)}
      />
      <text
        transform={`translate(${metrics.margin.small * 2}, ${metrics.margin.small})`}
        fontSize={metrics.font.large}
      >
        {Localization.get('Estimated Annual Emission from Power Generation with Chinese Capital')}
      </text>
      <g
        transform={`translate(${xOffset + metrics.emissionsInnerWidth - metrics.font.emCountries}, ${metrics.margin.small + 16 * 2})`}
        fontSize={metrics.font.emCountries}
        style={{ textTransform: 'uppercase' }}
        textAnchor='end'
      >
      {
        <text>
          {lineData.map((d, i) => (
            <tspan key={d.name} fill={d.color} dx={metrics.font.emCountries}>{Localization.get(d.name.toUpperCase())}</tspan>
          ))}
        </text>
      }
      </g>
      <g transform={`translate(${xOffset}, ${metrics.margin.small * 2 + metrics.emissionsInnerHeight})`}>
        <line
          transform={`translate(${-metrics.margin.small}, 0)`}
          y2={-metrics.emissionsInnerHeight}
          stroke='#B2B2B2'
        />
        {yScale.ticks().map((y, i) => (
          <g
            key={y}
            transform={`translate(${-metrics.margin.small}, ${-yScale(y)})`}
            textAnchor='end'
            fontSize={metrics.font.medium}
          >
            <text
              dx={-metrics.font.medium}
              dy={metrics.font.medium / 3}
            >
              {i % 2 === 0 ? (y === 0 ? `0 ${Localization.get('K Ton')}` : formatAs.number(y)) : null}
            </text>
            <line
              x2={y === 0 ? metrics.emissionsInnerWidth + metrics.margin.small * 2 : metrics.font.medium}
              stroke='#B2B2B2'
            />
          </g>
        ))}
        <g
          textAnchor='middle'
          fontWeight='normal'
        >
          {xScale.ticks()
            .filter(x => x % 2 === 0)
            .map(x => (
              <text
                key={x}
                transform={`translate(${xScale(x)}, ${metrics.font.medium * 1.5})`}
                fontSize={metrics.font.medium}
              >
                {x}
              </text>
            ))
          }
        </g>
        {lineData.map(d => (
          <path
            key={d.name}
            d={lineGenerator(d.years)}
            stroke={d.color}
            strokeWidth={metrics.barWidth / 2}
            strokeLinejoin='round'
            strokeLinecap='round'
            fill='none'
            onMouseMove={e => hoverPath(e, d)}
            // onMouseOut={e => hoverPath(e, null)}
          />
        ))}
        {tooltipData ? (
          <g
            id='tooltip'
            transform={`translate(${xScale(tooltipData.year)}, ${-yScale(tooltipData.cumulativeEmissions)})`}
            style={{ pointerEvents: 'none' }}
          >
            <line x2={-(xScale(tooltipData.year) + metrics.margin.small)} stroke='#191919' />
            <line y2={yScale(tooltipData.cumulativeEmissions)} stroke='#191919' />
            <circle r={metrics.barWidth / 3} strokeWidth='2' fill='white' stroke='black' />
          </g>
        ) : null}
      </g>
    </g>
  );
}
